import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { UtilsService } from '../../services/utils.service';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { SentimentTaxonomyDialogComponent } from '../sentiment-taxonomy-dialog/sentiment-taxonomy-dialog.component';
import { translateSourceType, translateTopic } from '../../../client/components/analytics/shared/taxonomy.util';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { take } from 'rxjs/internal/operators/take';
import { map } from 'rxjs/internal/operators/map';
import { Observable } from 'rxjs/internal/Observable';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	ClipTagRequest,
	CreateSentimentRequest,
	ManagementClipControllerService,
	ManagementClipResponse,
	ManagementSentimentControllerService,
	ManagementSentimentResponse,
	ManagementTagResponse,
} from 'api-clipping';
import { TagsDropAssignService } from '../../services/tags-drop-assign.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedService } from '../../services/shared.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Constants } from '../../interfaces/constants';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import {
	ContractAnnouncementTagRequest,
	ContractNoticeTagRequest,
	ManagementBankruptcyResponse,
	ManagementContractAnnouncementControllerService,
	ManagementContractAnnouncementResponse,
	ManagementContractNoticeAndAnnouncementResponse,
	ManagementContractNoticeResponse,
	ManagementSaleResponse,
	TagSuggestionResponse,
} from 'api-contract-notices';
import {
	ManagementTagTranscriptionControllerService,
	ManagementTranscriptionControllerService,
	TranscriptionResponse,
} from 'api-transcription';
import { ManagementContractNoticeControllerService } from 'api-contract-notices';
import { Subject } from 'rxjs/internal/Subject';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { finalize } from 'rxjs/internal/operators/finalize';
import { environment } from '../../../../environments/environment';

interface ManagementClipResponseWithSelection extends ManagementClipResponse {
	checked?: boolean;
	expanded?: boolean;
	relatedClips?: ManagementClipResponseWithSelection[];
}

interface ManagementContractNoticeResponseWithSelection extends ManagementContractNoticeResponse {
	checked?: boolean;
	expanded?: boolean;
	relatedClips?: ManagementClipResponseWithSelection[];
}

interface ManagementContractAnnouncementResponseWithSelection extends ManagementContractAnnouncementResponse {
	checked?: boolean;
	expanded?: boolean;
	relatedClips?: ManagementClipResponseWithSelection[];
}

interface ManagementContractNoticeAndAnnouncementResponseWithSelection extends ManagementContractNoticeAndAnnouncementResponse {
	tags?: any[];
	checked?: boolean;
	expanded?: boolean;
	relatedClips?: ManagementClipResponseWithSelection[];
}

interface TranscriptionResponseWithSelection extends TranscriptionResponse {
	checked?: boolean;
	expanded?: boolean;
}

type ListItemsWithSelection =
	| ManagementClipResponseWithSelection
	| ManagementContractNoticeResponseWithSelection
	| ManagementContractAnnouncementResponseWithSelection
	| ManagementContractNoticeAndAnnouncementResponseWithSelection
	| TranscriptionResponseWithSelection;

@Component({
	selector: 'app-list-card-view',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatCardModule,
		MatDividerModule,
		MatButtonModule,
		MatChipsModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule,
		MatExpansionModule,
		MatBadgeModule,
		CdkDropList,
		MatProgressSpinnerModule,
		MatMenuModule,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './list-card-view.component.html',
	styleUrl: './list-card-view.component.scss',
})
export class ListCardViewComponent implements OnChanges {
	@Input() role: 'admin' | 'client' | 'landing' = 'admin';
	@Input() listItems: ListItemsWithSelection[] = [];
	@Input() itemsPerPage: number = 30;
	@Input() preselectedItems: ListItemsWithSelection[] = [];
	@Input() appendModeActive: boolean = false;
	@Input() highlightActive: string = '';
	// Autotag UI feature flag (backend `autotag.ui.enabled`), contract-notices only. Gates the
	// color badge/border, the gray suggested-tag row, and the "reviewed" button so the card looks
	// exactly as today (only the existing auto-tagged-content classes) when it's false.
	@Input() autotagUiEnabled: boolean = false;
	public tagsUsed: any[] = [];
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();
  externalAssets = environment.reseller.externalAssets;
  externalAssetsRoot = environment.reseller.externalAssetsRoot;
	dataAdministrationType: 'CLIPPING' | 'CONTRACT_NOTICES' | 'CONTRACT_ANNOUNCEMENTS' | 'SALES' | 'BANKRUPTCIES' | 'ANALYTICS' | 'TRANSCRIPTIONS' = 'CLIPPING';

	apiLoading = {
		id: '',
		relatedClips: false,
	};

	// Guards assignTags() against a double drag-and-drop/double "+"-click sending two identical
	// PUT /v1/management/clips/tags requests for the same item before the first one's response
	// comes back - on the clipping side, two such requests each independently queue and send
	// their own "Реално време" e-mail (backend fix: real_time_send_queue dedup + a per
	// (group,tag,client) send log). This is the frontend's share of that fix, matching the bulk
	// tag editor's existing apiLoading boolean, just scoped per item id since this component
	// can have several cards being tagged independently at once.
	private pendingTagAssignItemIds = new Set<string>();

	displayedListItems: ListItemsWithSelection[] = [];

	displayDeleteIcon!: string;

	public selectedItems: any[] = [];

	private changeSentimentRatingSubject: Subject<any> = new Subject();
	private sentimentRatingStore: number | undefined = undefined;

	constructor(
		private route: ActivatedRoute,
		public utils: UtilsService,
		public tagsDropAssign: TagsDropAssignService,
		private apiClipping: ManagementClipControllerService,
		private apiContractNotices: ManagementContractNoticeControllerService,
		private apiContractAnnouncements: ManagementContractAnnouncementControllerService,
		private apiClippingSentiment: ManagementSentimentControllerService,
		private apiTranscriptionTags: ManagementTagTranscriptionControllerService,
		private apiTranscriptions: ManagementTranscriptionControllerService,
		private changeDetection: ChangeDetectorRef,
		private sharedService: SharedService,
		private translate: TranslateService,
		private dialog: MatDialog
	) {
		this.route.params.pipe(takeUntilDestroyed()).subscribe(params => {
			this.dataAdministrationType = Constants.ADMINISTRATION_DATA_TYPES.get(params['type']) || 'CLIPPING';
		});

		this.tagsDropAssign.updateTagDropzone.pipe(takeUntilDestroyed()).subscribe(response => {
			if (response.location === 'TAGZONE-LIST') {
				this.assignTags(response.itemId, response.tags);
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['listItems']) {
			if (!this.appendModeActive) {
				this.displayedListItems = this.listItems;
			} else {
				this.displayedListItems.push.apply(this.displayedListItems, this.listItems);
			}
			if (this.autotagUiEnabled && this.dataAdministrationType === 'CONTRACT_NOTICES') {
				this.listItems.forEach(item => this.loadTagSuggestions(item as ManagementContractNoticeAndAnnouncementResponseWithSelection));
			}
		} else if (changes['preselectedItems']) {
			this.selectedItems = this.preselectedItems;
			this.actionRequested.emit({ action: 'UPDATE_SELECTED', selected: this.selectedItems });
		}
	}

	/** Autotag UI - "gray tags" suggestion source (point б/в of the task). */
	private tagSuggestionsCache = new Map<string, TagSuggestionResponse[]>();

	private loadTagSuggestions(item: ManagementContractNoticeAndAnnouncementResponseWithSelection) {
		// Only notices carry the endpoint - announcements don't (see spec point 5).
		if (!item.id || !(item as ManagementContractNoticeAndAnnouncementResponse).noticeType || this.tagSuggestionsCache.has(item.id)) {
			return;
		}
		this.tagSuggestionsCache.set(item.id, []); // marks as "loading" so we don't re-request
		this.apiContractNotices
			.getTagSuggestions(item.id)
			.pipe(take(1))
			.subscribe({
				next: suggestions => {
					this.tagSuggestionsCache.set(item.id as string, suggestions || []);
					this.changeDetection.detectChanges();
				},
				error: () => {
					// leave the empty-array "loading" marker in place - avoids hammering a 404/500
					// endpoint on every re-render while still failing silently for the UI.
				},
			});
	}

	/** Suggestions not already applied as a real tag on this item - what the gray row shows. */
	getTagSuggestionsForItem(item: ManagementContractNoticeAndAnnouncementResponseWithSelection): TagSuggestionResponse[] {
		if (!item.id) return [];
		const suggestions = this.tagSuggestionsCache.get(item.id) || [];
		const existingTagIds = new Set((item.tags || []).map((tag: any) => tag.id));
		return suggestions.filter(suggestion => suggestion.tagId && !existingTagIds.has(suggestion.tagId));
	}

	/**
	 * COPY: "слично на: {наслов}, сличност {similarity}%". AI: no free-text reason field exists on
	 * TagSuggestionResponse (only modelVersion/confidence - see spec point б), so falls back to
	 * "AI сигурност: {confidence}%" rather than inventing a field the backend doesn't return.
	 * Assumes similarity/confidence are 0-1 fractions (consistent with riskScore's documented
	 * 0-1 range) and renders them as a rounded percentage.
	 */
	getTagSuggestionTooltip(suggestion: TagSuggestionResponse): string {
		if (suggestion.source === 'COPY') {
			return this.translate.instant('CONTRACT_NOTICES.TAG_SUGGESTION_COPY_REASON', {
				title: suggestion.similarTenderTitle || '',
				similarity: suggestion.similarity != null ? Math.round(suggestion.similarity * 100) : '?',
			});
		}
		return this.translate.instant('CONTRACT_NOTICES.TAG_SUGGESTION_AI_REASON', {
			confidence: suggestion.confidence != null ? Math.round(suggestion.confidence * 100) : '?',
		});
	}

	/** Gray-tag "+" click - adds immediately, no reason dialog (point б: "не бара отворање дијалог"). */
	addSuggestedTag(item: ManagementContractNoticeAndAnnouncementResponseWithSelection, suggestion: TagSuggestionResponse) {
		if (!item.id || !suggestion.tagId) return;
		this.assignTags(item.id, [{ id: suggestion.tagId, value: suggestion.tagName } as ManagementTagResponse], true);
	}

	/** Autotag UI "Прегледан" button (point в) - single contract notice only. */
	markReviewed(item: ManagementContractNoticeAndAnnouncementResponseWithSelection) {
		this.actionRequested.emit({ action: 'REVIEW', item: this.utils.suppressType(item) });
	}

	getColorBadgeClass(item: ListItemsWithSelection): string {
		if (!this.autotagUiEnabled || this.dataAdministrationType !== 'CONTRACT_NOTICES') return '';
		const color = (item as ManagementContractNoticeAndAnnouncementResponseWithSelection).color;
		return color === 3 ? 'autotag-color-3-content' : color === 4 ? 'autotag-color-4-content' : '';
	}

	getDisplayFieldByDataType(
		fieldName: string,
		item?:
			| ManagementClipResponseWithSelection
			| ManagementContractNoticeResponseWithSelection
			| ManagementContractAnnouncementResponseWithSelection
			| ManagementContractNoticeAndAnnouncementResponseWithSelection
			| TranscriptionResponseWithSelection
	) {
		// '', '', 'CONTRACT_AWARD_NOTICES', 'SALES', 'BANKRUPTCIES'
		switch (this.dataAdministrationType) {
			case 'CLIPPING': {
				if (fieldName === 'id') {
					return (item as ManagementClipResponseWithSelection).main ? (item as ManagementClipResponseWithSelection).groupId : (item as ManagementClipResponseWithSelection).id;
				} else if (fieldName === 'initialDate') {
					return this.utils.formatDate((item as ManagementClipResponseWithSelection).publicationDate);
				} else if (fieldName === 'medium') {
					return this.utils.getParameterByLanguageScript((item as ManagementClipResponseWithSelection).medium, 'name');
				} else if (fieldName === 'title') {
					return this.utils.getParameterByLanguageScript(item, fieldName, true);
				} else if (fieldName === 'brief') {
					// return this.utils.truncateText(this.utils.getParameterByLanguageScript(item, fieldName, true), 350);
          			return this.utils.getParameterByLanguageScript(item, fieldName, true);
				} else if (fieldName === 'height') {
					return '75px';
				}
				break;
			}
			case 'CONTRACT_NOTICES':
			case 'CONTRACT_ANNOUNCEMENTS': {
				const itemTypeSafe: ManagementContractNoticeAndAnnouncementResponse = item as ManagementContractNoticeAndAnnouncementResponse;
				if (fieldName === 'id') {
					return itemTypeSafe.id;
				} else if (fieldName === 'title') {
					if (itemTypeSafe.noticeType) {
						return this.utils.getParameterByLanguageScript(item, 'contractSubject', true);
					} else if (itemTypeSafe.announcementType) {
						return this.utils.getParameterByLanguageScript(itemTypeSafe.contractNotice, 'contractSubject', true);
					}
				} else if (fieldName === 'medium') {
					let mediums: string[] = [];
					if (itemTypeSafe.noticeType) {
						if (itemTypeSafe.mediums) {
							itemTypeSafe.mediums.forEach((medium: any) => {
								mediums.push(this.utils.getParameterByLanguageScript(medium, 'name'));
							});
						}
					} else if (itemTypeSafe.announcementType) {
						if (itemTypeSafe.contractNotice && itemTypeSafe.contractNotice.mediums) {
							itemTypeSafe.contractNotice.mediums.forEach((medium: any) => {
								mediums.push(this.utils.getParameterByLanguageScript(medium, 'name'));
							});
						}
					}
					return mediums;
				} else if (fieldName === 'country') {
					if (itemTypeSafe.noticeType) {
						return itemTypeSafe.country ? itemTypeSafe.country.toLowerCase() : '';
					} else {
						return itemTypeSafe.contractNotice?.country ? itemTypeSafe.contractNotice?.country.toLowerCase() : '';
					}
				} else if (fieldName === 'publishedDate' || fieldName === 'initialDate') {
					return this.utils.formatDate(itemTypeSafe.publishedDate);
				} else if (fieldName === 'deliveryDate') {
					if (itemTypeSafe.noticeType && itemTypeSafe.deliveryDate) {
						return this.utils.formatDate(itemTypeSafe.deliveryDate);
					} else if (itemTypeSafe.announcementType && itemTypeSafe.contractNotice && itemTypeSafe.contractNotice.deliveryDate) {
						return this.utils.formatDate(itemTypeSafe.contractNotice.deliveryDate);
					}
					return null;
				} else if (fieldName === 'content' || fieldName === 'brief') {
					if (itemTypeSafe.noticeType) {
						return this.utils.getParameterByLanguageScript(itemTypeSafe.contractAuthority, 'name', true);
					} else {
						return this.utils.getParameterByLanguageScript(itemTypeSafe.contractNotice?.contractAuthority, 'name', true);
					}
				} else if (fieldName === 'type') {
					if (itemTypeSafe.noticeType) {
						return this.translate.instant('CONTRACT_NOTICES.' + itemTypeSafe.noticeType);
					} else if (itemTypeSafe.announcementType) {
						return this.translate.instant('CONTRACT_ANNOUNCEMENTS.' + itemTypeSafe.announcementType);
					}
				} else if (fieldName === 'estimatedPurchaseValue') {
					if (itemTypeSafe.noticeType) {
						return itemTypeSafe.estimatedPurchaseValue ? `${this.utils.formatCurrency(itemTypeSafe.estimatedPurchaseValue.toString())} ${itemTypeSafe.currency}` : '';
					} else if (itemTypeSafe.announcementType) {
						return itemTypeSafe.contractNotice?.estimatedPurchaseValue
							? `${this.utils.formatCurrency(itemTypeSafe.contractNotice.estimatedPurchaseValue.toString())} ${itemTypeSafe.contractNotice.currency}`
							: '';
					}
				} else if (fieldName === 'divisiblePurchase') {
					if (itemTypeSafe.noticeType) {
						return itemTypeSafe.divisiblePurchase;
					} else if (itemTypeSafe.announcementType) {
						return itemTypeSafe.contractNotice?.divisiblePurchase;
					}
				} else if (fieldName === 'procurementType') {
					if (itemTypeSafe.noticeType) {
						return (itemTypeSafe as any).procurementType;
					} else if (itemTypeSafe.announcementType) {
						return itemTypeSafe.contractNotice?.procurementType;
					}
				} else if (fieldName === 'height') {
					return '50px';
				}
				break;
			}
			case 'SALES': {
				const itemTypeSafe: ManagementSaleResponse = item as ManagementSaleResponse;
				if (fieldName === 'id') {
					return itemTypeSafe.id;
				} else if (fieldName === 'initialDate') {
					return this.utils.formatDate(itemTypeSafe.publicationDate);
				} else if (fieldName === 'title') {
					const trustees = itemTypeSafe.trustees?.map(t => this.utils.getParameterByLanguageScript(t, 'name', true));
					return trustees?.join(', ');
				} else if (fieldName === 'brief') {
					const debtors = itemTypeSafe.debtors?.map(t => this.utils.getParameterByLanguageScript(t, 'name', true));
					return debtors?.join(', ');
				} else if (fieldName === 'height') {
					return '50px';
				}
				break;
			}
			case 'BANKRUPTCIES': {
				const itemTypeSafe: ManagementBankruptcyResponse = item as ManagementBankruptcyResponse;
				if (fieldName === 'id') {
					return itemTypeSafe.id;
				} else if (fieldName === 'initialDate') {
					return this.utils.formatDate(itemTypeSafe.publicationDate);
				} else if (fieldName === 'title') {
					return this.translate.instant('BANKRUPTCIES.PHASES.' + itemTypeSafe.phase);
				} else if (fieldName === 'brief') {
					return this.utils.getParameterByLanguageScript(itemTypeSafe.company, 'name', true);
				} else if (fieldName === 'height') {
					return '50px';
				}
				break;
			}
			case 'TRANSCRIPTIONS': {
				const itemTypeSafe: TranscriptionResponseWithSelection = item as TranscriptionResponseWithSelection;
				if (fieldName === 'id') {
					return itemTypeSafe.id;
				} else if (fieldName === 'initialDate') {
					// TEMP: publicationDate isn't in the generated api-transcription SDK yet, even
					// though the backend response already carries it for YouTube-sourced items.
					// Falls back to createdAt for older/non-YouTube transcriptions.
					const publicationDate = (itemTypeSafe as unknown as { publicationDate?: string }).publicationDate;
					return this.utils.formatDate(publicationDate || itemTypeSafe.createdAt);
				} else if (fieldName === 'title') {
					return itemTypeSafe.fileName;
				} else if (fieldName === 'brief') {
					if (itemTypeSafe.content && itemTypeSafe.content.length > 0) {
						const allText = itemTypeSafe.content.map(c => c.text || '').join(' ').trim();
						return this.utils.truncateText(allText, 700);
					}
					return '';
				} else if (fieldName === 'lang') {
					return itemTypeSafe.lang ? itemTypeSafe.lang.toLowerCase() : '';
				} else if (fieldName === 'height') {
					return '75px';
				}
				break;
			}
		}
	}

	checkItemHighlight(item: ListItemsWithSelection) {
		return this.highlightActive.includes(this.getDisplayFieldByDataType('id', item)) ? 'highlight' : null;
	}

	hasRelatedClips(item: ListItemsWithSelection): boolean {
		return 'relatedClips' in item;
	}

	// Repeat TV airings ("Повторени прилози") - only for a TV clip logged as having aired more
	// than once (see NewsItemAiring on the backend). Null renders nothing, same as before.
	airingTimesLabel(item: ListItemsWithSelection): string | null {
		const typed = item as any;
		const times: string[] | undefined = typed?.airingTimes;
		if (!times || times.length < 2 || typed?.medium?.type !== 'TV') {
			return null;
		}
		return this.translate.instant('CLIPS.AIRED_TIMES', { count: times.length, times: times.join(', ') });
	}

	isPendingTranscription(item: ListItemsWithSelection): boolean {
		const orderId = (item as any)?.orderId;
		return typeof orderId === 'string' && orderId.startsWith('PENDING-');
	}

	getItemRelatedClips(item: ListItemsWithSelection): ManagementClipResponseWithSelection[] | undefined {
		return 'relatedClips' in item ? (item as any).relatedClips : undefined;
	}

	openMediumLink(
		item:
			| ManagementClipResponseWithSelection
			| ManagementContractNoticeResponseWithSelection
			| ManagementContractAnnouncementResponseWithSelection
			| ManagementContractNoticeAndAnnouncementResponseWithSelection
			| TranscriptionResponseWithSelection,
		providedUrl?: string
	) {
		// this.itemAction('EDIT_LIST_ITEM', item);
		let url = providedUrl ? providedUrl : ('url' in item ? item.url : undefined) || (item as ManagementContractNoticeResponseWithSelection).fileUrl;
		if (!url) {
			if (this.dataAdministrationType === 'CLIPPING') {
				url = (item as ManagementClipResponseWithSelection).mediaUrl;
			} else if (this.dataAdministrationType === 'TRANSCRIPTIONS') {
				url = (item as TranscriptionResponseWithSelection).fileUrl;
			} else {
				this.itemAction('OPEN_REPORT', item, 'SINGLE');
				return;
			}
		}

		const fileUrl = (item as ManagementContractNoticeResponseWithSelection).fileUrl;
		const itemUrl = 'url' in item ? item.url : undefined;
		if (!providedUrl && !itemUrl && fileUrl && this.isImageUrl(fileUrl)) {
			this.itemAction('OPEN_REPORT', item, 'SINGLE');
			return;
		}

		window.open(url, '_blank');
	}

	isImageUrl(url: string): boolean {
		const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
		const urlLower = url.toLowerCase();
		return imageExtensions.some(ext => urlLower.includes(ext));
	}

  openScreenshootLink(item: ManagementClipResponseWithSelection) {
    if (item.screenshotUrl) {
      window.open(`${this.externalAssetsRoot}${item.screenshotUrl}`, '_blank');
    } else {
      this.sharedService.showNotification.next({
        message: this.translate.instant('NOTIFICATIONS.SCREENSHOT_NOT_FOUND'),
        type: 'ERROR',
      });
    }
  }

	private openAllNewsLinks(clip: ManagementClipResponseWithSelection) {
		this.openMediumLink(clip);
		if (clip.relatedCount && clip.groupId) {
			if (clip.relatedClips && clip.relatedClips.length) {
				clip.relatedClips.forEach(relatedClip => {
					this.openMediumLink(relatedClip);
				});
			} else {
				this.apiClipping
					.getClips(clip.groupId, clip.clientId || undefined)
					.pipe(take(1))
					.subscribe({
						next: response => {
							response.forEach((relatedClip: any) => {
								this.openMediumLink(relatedClip);
							});
						},
					});
			}
		}
	}

	/** Tags management methods */
	/**
	 * `skipReasonPrompt` is true only for the autotag UI's one-click gray-tag "+" (point б: adds
	 * immediately, no dialog). Every other caller (drag-and-drop) goes through the reason dialog
	 * when `autotagUiEnabled` is on and the target is a contract notice (point г) - everyone else
	 * (clipping, transcriptions, announcements) is unaffected and behaves exactly as before.
	 */
	private assignTags(itemId: string, tags: ManagementTagResponse[], skipReasonPrompt = false) {
		const itemToAddTags = (this.displayedListItems as any[]).find(item => item.groupId === itemId || item.id === itemId);
		if (!itemToAddTags) return;

		// Nothing new to add - matches the backend's own "already attached" no-op, and avoids a
		// wasted request. Checked before the in-flight guard below so a redundant drop never
		// blocks a later, genuinely new tag on the same item.
		if (tags.every(tag => itemToAddTags.tags?.some((existing: ManagementTagResponse) => existing.id === tag.id))) {
			return;
		}

		// A request for this exact item is already on its way - a second drop/click while it's
		// pending must be ignored, not queued, so it can never become a second identical PUT.
		if (this.pendingTagAssignItemIds.has(itemToAddTags.id)) {
			return;
		}
		this.pendingTagAssignItemIds.add(itemToAddTags.id);
		const releaseGuard = () => this.pendingTagAssignItemIds.delete(itemToAddTags.id);

		const itemTypeSafe: ManagementContractNoticeAndAnnouncementResponse = itemToAddTags as ManagementContractNoticeAndAnnouncementResponse;
		const isNoticePromptable = !skipReasonPrompt && this.autotagUiEnabled && this.dataAdministrationType === 'CONTRACT_NOTICES' && !!itemTypeSafe.noticeType;

		const proceed = (reason: { reasonCode?: string; userReason?: string } | null) => {
			if (reason === null) {
				releaseGuard(); // dialog cancelled - no request was ever sent
				return;
			}

			const tagDto: ClipTagRequest | ContractNoticeTagRequest | ContractAnnouncementTagRequest = {
				dataIds: [itemToAddTags.id],
				tagIds: tags.map(item => item.id),
			};

			switch (this.dataAdministrationType) {
				case 'CLIPPING': {
					this.apiClipping
						.addTags(tagDto)
						.pipe(take(1), finalize(releaseGuard))
						.subscribe({
							next: () => {
								this.tagsAddedOnContent(this.displayedListItems as ManagementClipResponseWithSelection[], itemToAddTags as ManagementClipResponseWithSelection, tags);
							},
						});
					break;
				}
				case 'CONTRACT_NOTICES':
				case 'CONTRACT_ANNOUNCEMENTS': {
					if (itemTypeSafe.announcementType) {
						// Announcement type
						this.apiContractAnnouncements
							.addContractAnnouncementTags(tagDto)
							.pipe(take(1), finalize(releaseGuard))
							.subscribe({
								next: () => {
									this.tagsAddedOnContent(
										this.displayedListItems as ManagementContractAnnouncementResponseWithSelection[],
										itemToAddTags as ManagementClipResponseWithSelection,
										tags
									);
								},
							});
					} else if (itemTypeSafe.noticeType) {
						// Notice type
						this.apiContractNotices
							.addContractNoticeTags({ ...tagDto, ...(reason || {}) } as ContractNoticeTagRequest)
							.pipe(take(1), finalize(releaseGuard))
							.subscribe({
								next: () => {
									this.tagsAddedOnContent(
										this.displayedListItems as ManagementContractNoticeAndAnnouncementResponseWithSelection[],
										itemToAddTags as ManagementClipResponseWithSelection,
										tags
									);
								},
							});
					} else {
						releaseGuard(); // neither branch matched - no request was sent
					}
					break;
				}
				case 'TRANSCRIPTIONS': {
					const transcriptionItem = itemToAddTags as TranscriptionResponseWithSelection;
					if (transcriptionItem.id) {
						this.apiTranscriptions
							.addTranscriptionsTags({
								dataIds: [transcriptionItem.id],
								tagIds: tags.map(tag => tag.id || '')
							})
							.pipe(take(1), finalize(releaseGuard))
							.subscribe({
								next: () => {
									this.sharedService.showNotification.next({
										message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
										type: 'SUCCESS',
									});
									this.tagsAddedOnContent(this.displayedListItems as TranscriptionResponseWithSelection[], transcriptionItem, tags);
								},
								error: () => {
									this.sharedService.showNotification.next({
										message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
										type: 'ERROR',
									});
								},
							});
					} else {
						releaseGuard(); // no id on the item - no request was sent
					}
					break;
				}
				default: {
					releaseGuard();
				}
			}
		};

		if (isNoticePromptable) {
			this.promptTagReason().subscribe(reason => proceed(reason));
		} else {
			proceed({});
		}
	}

	/**
	 * Fixed 5-code quick-reason list (Constants.TAG_SUGGESTION_REASON_CODES) plus an optional
	 * free-text comment - closes with `null` if the dialog was cancelled (X / outside click),
	 * `{}`-shaped data otherwise. Neither field is required, matching the spec: the dialog must
	 * be closable/saveable with no text entered.
	 */
	private promptTagReason(): Observable<{ reasonCode?: string; userReason?: string } | null> {
		return this.dialog
			.open(ConfirmationDialogComponent, {
				width: '480px',
				data: {
					type: 'custom-fields',
					mode: 'CONFIRM',
					message: 'CONTRACT_NOTICES.TAG_REASON_DIALOG_TITLE',
					fields: [
						{
							type: 'selection',
							key: 'reasonCode',
							label: 'CONTRACT_NOTICES.TAG_REASON_DIALOG_TITLE',
							standalone: true,
							data: Constants.TAG_SUGGESTION_REASON_CODES.map(r => ({ value: r.code, label: this.translate.instant(r.label) })),
						},
						{ type: 'textarea', key: 'userReason' },
					],
				},
			})
			.afterClosed()
			.pipe(
				take(1),
				map((result: any) => (result ? { reasonCode: result.data?.reasonCode, userReason: result.data?.userReason } : null))
			);
	}

	private tagsAddedOnContent(
		listItems: ManagementClipResponseWithSelection[] | ManagementContractNoticeAndAnnouncementResponseWithSelection[] | ManagementContractAnnouncementResponseWithSelection[] | TranscriptionResponseWithSelection[],
		itemToAddTags: ManagementClipResponseWithSelection | ManagementContractNoticeAndAnnouncementResponseWithSelection | ManagementContractAnnouncementResponseWithSelection | TranscriptionResponseWithSelection,
		tags: ManagementTagResponse[]
	) {
		this.updateTagsOnDisplayedContent(listItems, itemToAddTags, tags);
		this.changeDetection.detectChanges();

		this.actionRequested.emit({
			action: 'TOOLBAR',
			groupAction: 'ADD_TAGS',
			tags,
		});
	}
	private updateTagsOnDisplayedContent(
		listItems: ManagementClipResponseWithSelection[] | ManagementContractNoticeAndAnnouncementResponseWithSelection[] | ManagementContractAnnouncementResponseWithSelection[] | TranscriptionResponseWithSelection[],
		item: ManagementClipResponseWithSelection | ManagementContractNoticeAndAnnouncementResponseWithSelection | ManagementContractAnnouncementResponseWithSelection | TranscriptionResponseWithSelection,
		tags: ManagementTagResponse[]
	) {
		listItems.some(listItem => {
			if ('relatedClips' in listItem && listItem.relatedClips?.length) {
				this.updateTagsOnDisplayedContent(listItem.relatedClips, item, tags);
			}

			if (
				listItem.id === item.id ||
				// For clipping related clips
				((listItem as ManagementClipResponseWithSelection).groupId &&
					(listItem as ManagementClipResponseWithSelection).groupId === (item as ManagementClipResponseWithSelection).groupId)
			) {
				tags.forEach(tag => {
					if (!listItem.tags?.find(existingTag => existingTag.id === tag.id)) {
						listItem.tags?.push(tag);
					}
				});
				return listItem.id === item.id;
			}
			return false;
		});
	}

	removeTag(
		item: ManagementClipResponseWithSelection | ManagementContractNoticeAndAnnouncementResponseWithSelection | ManagementContractAnnouncementResponseWithSelection | TranscriptionResponseWithSelection,
		tag: ManagementTagResponse
	) {
		if (!item.id) return;

		// See assignTags() above for the same reason-dialog gating (point г).
		const itemTypeSafe: ManagementContractNoticeAndAnnouncementResponse = item as ManagementContractNoticeAndAnnouncementResponse;
		const isNoticePromptable = this.autotagUiEnabled && this.dataAdministrationType === 'CONTRACT_NOTICES' && !!itemTypeSafe.noticeType;

		const proceed = (reason: { reasonCode?: string; userReason?: string } | null) => {
			if (reason === null) return; // dialog cancelled

			const tagDto: ClipTagRequest | ContractNoticeTagRequest | ContractAnnouncementTagRequest = {
				dataIds: [item.id as string],
				tagIds: [tag.id],
			};

			switch (this.dataAdministrationType) {
				case 'CLIPPING': {
					this.apiClipping
						.removeTags(tagDto)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.tagsRemovedFromContent(item as ManagementClipResponseWithSelection, tag);
							},
						});
					break;
				}
				case 'CONTRACT_NOTICES':
        		case 'CONTRACT_ANNOUNCEMENTS':{
					if (itemTypeSafe.announcementType) {
						// Announcement type
						this.apiContractAnnouncements
							.removeContractAnnouncementTags(tagDto)
							.pipe(take(1))
							.subscribe({
								next: () => {
									this.tagsRemovedFromContent(item as ManagementContractAnnouncementResponseWithSelection, tag);
								},
							});
					} else if (itemTypeSafe.noticeType) {
						// Notice type
						this.apiContractNotices
							.removeContractNoticeTags({ ...tagDto, ...(reason || {}) } as ContractNoticeTagRequest)
							.pipe(take(1))
							.subscribe({
								next: () => {
									this.tagsRemovedFromContent(item as ManagementContractNoticeAndAnnouncementResponseWithSelection, tag);
								},
							});
					}
					break;
				}
				case 'TRANSCRIPTIONS': {
					this.apiTranscriptions
						.removeTranscriptionsTags({
							dataIds: [item.id as string],
							tagIds: [tag.id || '']
						})
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
									type: 'SUCCESS',
								});
								this.tagsRemovedFromContent(item as TranscriptionResponseWithSelection, tag);
							},
							error: () => {
								this.sharedService.showNotification.next({
									message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
									type: 'ERROR',
								});
							},
						});
					break;
				}
			}
		};

		if (isNoticePromptable) {
			this.promptTagReason().subscribe(reason => proceed(reason));
		} else {
			proceed({});
		}
	}
	private tagsRemovedFromContent(
		item: ManagementClipResponseWithSelection | ManagementContractNoticeAndAnnouncementResponseWithSelection | ManagementContractAnnouncementResponseWithSelection | TranscriptionResponseWithSelection,
		tag: ManagementTagResponse
	) {
		if (item.tags) {
			item.tags = item.tags?.filter(tagItem => tagItem.id !== tag.id);

			if ('relatedClips' in item && item.relatedClips?.length) {
				item.relatedClips.forEach(relatedClip => {
					relatedClip.tags = relatedClip.tags?.filter(tagItem => tagItem.id !== tag.id);
				});
			}
		}
		this.changeDetection.detectChanges();
	}

	getSentimentRating(sentimentType: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE', item: ManagementClipResponse, type: 'ICON' | 'CLASS') {
		const score = item.sentiment?.sentimentScore;
		const currentType = score == null ? undefined : score > 0 ? 'POSITIVE' : score < 0 ? 'NEGATIVE' : 'NEUTRAL';
		const active = sentimentType === currentType ? ' active' : '';
		switch (sentimentType) {
			case 'POSITIVE': {
				if (currentType === sentimentType) {
					return type === 'ICON' ? 'sentiment_very_satisfied' : 'positive' + active;
				}
				break;
			}
			case 'NEUTRAL': {
				if (currentType === sentimentType) {
					return type === 'ICON' ? 'sentiment_neutral' : 'neutral' + active;
				}
				break;
			}
			case 'NEGATIVE': {
				if (currentType === sentimentType) {
					return type === 'ICON' ? 'sentiment_very_dissatisfied' : 'negative' + active;
				}
				break;
			}
		}
		return '';
	}

  /**
   * Thumbs up adds 25, thumbs down subtracts 25 from the current Influence, "–" resets it to
   * 0 - always clamped to this clip's own Visibility (and to -100/+100), instead of jumping
   * straight to a fixed +-25 the way this used to work.
   */
  createUpdateSentimentRating(sentimentType: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE', item: ManagementClipResponse) {
		const currentScore = item.sentiment?.sentimentScore ?? 0;
		let sentimentScore: number;
		if (sentimentType === 'NEUTRAL') {
			if (item.sentiment && currentScore === 0) return;
			sentimentScore = 0;
		} else {
			sentimentScore = this.clampInfluenceToVisibility(currentScore + (sentimentType === 'POSITIVE' ? 25 : -25), item.visibilityIndex);
			if (sentimentScore === currentScore) {
				this.showInfluenceClampedNotice(item.visibilityIndex);
				return;
			}
		}
		this.saveSentimentScore(item, sentimentScore);
	}

	/** Clamps to [-100, 100], then further to the clip's own Visibility (if it has one). */
	private clampInfluenceToVisibility(requestedScore: number, visibilityIndex?: number | null): number {
		let clamped = Math.max(-100, Math.min(100, Math.round(requestedScore)));
		if (visibilityIndex != null && visibilityIndex > 0) {
			const max = Math.min(visibilityIndex, 100);
			clamped = Math.max(-max, Math.min(max, clamped));
		}
		return clamped;
	}

	private showInfluenceClampedNotice(visibilityIndex?: number | null) {
		const message =
			visibilityIndex != null && visibilityIndex > 0
				? this.translate.instant('CLIPS.INFLUENCE_CLAMPED', { max: visibilityIndex })
				: this.translate.instant('CLIPS.INFLUENCE_CLAMPED_NO_VISIBILITY');
		this.sharedService.showNotification.next({
			message,
			type: 'WARNING',
		});
	}

	/** Saves an already-clamped score; `sentimentScore` is trusted to be within the rules. */
	private saveSentimentScore(item: ManagementClipResponse, sentimentScore: number) {
		const onSaved = (response: ManagementSentimentResponse) => {
			this.sharedService.showNotification.next({
				message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
				type: 'SUCCESS',
			});
			item.sentiment = response;
			this.syncGroupSentimentVisually(item);
			this.changeDetection.detectChanges();
		};
		if (item.sentiment && item.sentiment.id) {
			this.apiClippingSentiment.updateSentiment({ sentimentScore }, item.sentiment.id).pipe(take(1)).subscribe({ next: onSaved });
		} else if (item.id) {
			this.apiClippingSentiment.createSentiment({ sentimentScore, clipId: item.id, clientId: item.clientId }).pipe(take(1)).subscribe({ next: onSaved });
		}
	}

	/**
	 * The backend propagates a sentiment set on a MAIN clip to every clip in its group: each
	 * related clip adopts the main's direction (positive/neutral/negative) while keeping its own
	 * magnitude, defaulting to 25 when it had none, clamped to that sibling's own Visibility
	 * (see `applySentimentAcrossGroup` on the backend). Rebuild
	 * relatedClips with fresh references so the OnPush card re-renders them in place — no re-fetch,
	 * no full reload. Rating a related clip is a no-op here (only main clips propagate).
	 */
	private syncGroupSentimentVisually(mainClip: any) {
		if (!mainClip.relatedClips?.length) {
			return;
		}
		const sign = Math.sign(mainClip.sentiment?.sentimentScore ?? 0);
		mainClip.relatedClips = mainClip.relatedClips.map((clip: any) => {
			const magnitude = Math.abs(clip.sentiment?.sentimentScore ?? 0) || 25;
			const sentimentScore = sign === 0 ? 0 : this.clampInfluenceToVisibility(sign * magnitude, clip.visibilityIndex);
			return { ...clip, sentiment: { ...(clip.sentiment || {}), sentimentScore } };
		});
	}

	private editingMetrics = new Set<string>();

	private metricKey(item: ManagementClipResponse, field: 'visibility' | 'sentiment'): string {
		return `${item.id}:${field}`;
	}

	isEditingMetric(item: ManagementClipResponse, field: 'visibility' | 'sentiment'): boolean {
		return item.id ? this.editingMetrics.has(this.metricKey(item, field)) : false;
	}

	enterEditMetric(item: ManagementClipResponse, field: 'visibility' | 'sentiment') {
		if (!item.id) return;
		const key = this.metricKey(item, field);
		this.editingMetrics.add(key);
		setTimeout(() => {
			const el = document.querySelector(`input[data-edit-key="${key}"]`) as HTMLInputElement | null;
			el?.focus();
			el?.select();
		}, 0);
	}

	exitEditMetric(item: ManagementClipResponse, field: 'visibility' | 'sentiment') {
		if (!item.id) return;
		this.editingMetrics.delete(this.metricKey(item, field));
	}

	/** Manual Influence entry: -100..+100, clamped to this clip's own Visibility (rule 2). */
	setSentimentScore(item: ManagementClipResponse, rawValue: string | number | null) {
		if (rawValue === '' || rawValue === null || rawValue === undefined) return;
		const requestedScore = Number(rawValue);
		if (!Number.isFinite(requestedScore)) return;
		const sentimentScore = this.clampInfluenceToVisibility(requestedScore, item.visibilityIndex);
		if (item.sentiment?.sentimentScore === sentimentScore) {
			if (sentimentScore !== requestedScore) {
				this.showInfluenceClampedNotice(item.visibilityIndex);
			}
			return;
		}
		this.saveSentimentScore(item, sentimentScore);
		if (sentimentScore !== requestedScore) {
			this.showInfluenceClampedNotice(item.visibilityIndex);
		}
	}

	hasTaxonomyValues(item: ManagementClipResponse): boolean {
		return !!(item.sentiment?.sourceType || item.sentiment?.industryTopics?.length || item.sentiment?.corporateTopics?.length);
	}

	// Chips show the translated label; the underlying value stored on the clip/sentiment row
	// stays the fixed English AiTaxonomy label - same mapping as the taxonomy edit dialog and
	// the Analytics screens.
	translateSourceTypeLabel(source: string | null | undefined): string {
		return translateSourceType(source, this.translate);
	}

	translateTopicLabel(topic: string | null | undefined): string {
		return translateTopic(topic, this.translate);
	}

	// Only ever called when item.sentiment.id already exists (see the @if guard in the
	// template) - Source Type/Topics are AI-set on the same sentiment row as Visibility/
	// Influence, never created on their own: a clip with no sentiment row has no "/" row to
	// attach a manual taxonomy value to.
	openTaxonomyDialog(item: ManagementClipResponse) {
		if (!item.sentiment?.id) return;
		this.dialog
			.open(SentimentTaxonomyDialogComponent, {
				data: { sentiment: item.sentiment },
				width: '420px',
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe(response => {
				if (response) {
					item.sentiment = response;
					this.changeDetection.detectChanges();
				}
			});
	}

	updateVisibilityIndex(item: ManagementClipResponse, rawValue: string | number | null) {
		if (!item.id) return;
		if (rawValue === '' || rawValue === null || rawValue === undefined) return;
		const visibilityIndex = Number(rawValue);
		if (!Number.isFinite(visibilityIndex)) return;
		if (item.visibilityIndex === visibilityIndex) return;
		this.apiClipping
			.makeMainClipsInNonMainGroups({ clipId: item.id, visibilityIndex })
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					item.visibilityIndex = response.visibilityIndex;
					// The backend also shrinks an existing Influence that is now above the new
					// Visibility (rule 5) but this particular endpoint doesn't echo it back -
					// mirror that clamp here so the number shown doesn't lag behind the DB.
					if (item.sentiment && this.clampInfluenceToVisibility(item.sentiment.sentimentScore ?? 0, item.visibilityIndex) !== item.sentiment.sentimentScore) {
						item.sentiment = { ...item.sentiment, sentimentScore: this.clampInfluenceToVisibility(item.sentiment.sentimentScore ?? 0, item.visibilityIndex) };
					}
					this.changeDetection.detectChanges();
				},
			});
	}
	/** (END)Tags management methods */

	itemAction(type: 'EDIT_LIST_ITEM' | 'OPEN_REPORT' | 'OPEN_ALL_LINKS' | 'SET_AS_MAIN' | 'EXPORT_TRANSCRIPTION', item: ListItemsWithSelection, additionalData?: string) {
		switch (type) {
			case 'SET_AS_MAIN': {
				this.actionRequested.emit({
					action: 'SET_AS_MAIN',
					data: 'listItem',
					item,
				});
				break;
			}
			case 'EDIT_LIST_ITEM': {
				this.actionRequested.emit({
					action: 'LOAD',
					data: 'listItem',
					id: item.id,
					item,
				});
				break;
			}
			case 'OPEN_REPORT': {
				this.actionRequested.emit({
					action: 'GENERATE_REPORT',
					type: additionalData,
					data: 'listItem',
					item,
				});
				break;
			}
			case 'OPEN_ALL_LINKS': {
				this.openAllNewsLinks(item as ManagementClipResponseWithSelection);
				break;
			}
			case 'EXPORT_TRANSCRIPTION': {
				this.actionRequested.emit({
					action: 'EXPORT_TRANSCRIPTION',
					data: 'listItem',
					item,
				});
				break;
			}
		}
	}

	selectAll(checked: boolean, item: any) {
		item.checked = checked;
		this.checkItem(checked, item);

		if (item.main) {
			this.getRelatedClips(item, true);
			if (!item.relatedClips) {
				return;
			}
			item.relatedClips.forEach((relatedClip: any) => {
				relatedClip.checked = checked;
				this.checkItem(checked, relatedClip);
				// item.expanded = checked; // requested to remove auto expand on select all
				item.expanded = false;
			});
		}
	}
	checkItem(event: any, item: any) {
		if (event) {
			this.selectedItems.push(item);
		} else {
			this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.id !== item.id);
		}
		this.actionRequested.emit({ action: 'UPDATE_SELECTED', selected: this.selectedItems });
	}

	checkDuplicateMediumInRelated(mainClip: ListItemsWithSelection, releatedClip: ManagementClipResponseWithSelection) {
		if ('relatedClips' in mainClip && mainClip.relatedClips) {
			const duplicateMedium = mainClip.relatedClips?.filter(item => item.medium?.id === releatedClip.medium?.id);
			const isMainClipSameMedium = 'medium' in mainClip && mainClip.medium?.id === releatedClip.medium?.id;
			return (duplicateMedium && duplicateMedium.length > 1) || isMainClipSameMedium ? 'duplicate-medium' : '';
		}
		return '';
	}

	getContractNoticeIndicator(listItem: ManagementContractNoticeResponseWithSelection | ManagementContractAnnouncementResponseWithSelection, type: 'icon' | 'description') {
		if ((listItem as ManagementContractNoticeResponseWithSelection).divisiblePurchase) {
			return type === 'icon' ? 'content_copy' : 'CONTRACT_NOTICES.DIVISABLE_PURCHASE';
		}

		switch (listItem.type) {
			case 'CANCELLATION': {
				return type === 'icon' ? 'event_busy' : 'CONTRACT_ANNOUNCEMENTS.' + listItem.type;
			}
			case 'MODIFICATION': {
				return type === 'icon' ? 'event_available' : 'CONTRACT_ANNOUNCEMENTS.' + listItem.type;
			}
			case 'LATEST_DECISION': {
				return type === 'icon' ? 'pending_actions' : 'CONTRACT_ANNOUNCEMENTS.' + listItem.type;
			}
		}
		return '';
	}

	getSourceIcon(clip: ManagementClipResponseWithSelection) {
		switch (clip.source) {
			// add case for custom char
			default: {
				if (clip.source) {
					return clip.source.charAt(0);
				}
				return;
			}
		}
	}

	openCluster(clip: ManagementClipResponseWithSelection) {
		let url = Constants.CLUSTER_TYPES.find(cluster => cluster.id === clip.source)?.url;
		switch (clip.source) {
			case 'GRID': {
				url = `${url}${clip.groupId}/${clip.publicationDate}`;
				break;
			}
			case 'FAXAL': {
				url = `${url}${clip.groupId}/cluster`;
				break;
			}
			default: {
				return;
			}
		}
		window.open(url, '_blank');
	}

	getRelatedClips(item: ManagementClipResponseWithSelection, selectAll?: boolean) {
		if (!item.relatedClips && item.groupId) {
			this.apiLoading = {
				id: item.id,
				relatedClips: true,
			};
			// Emit loading state to parent
			this.actionRequested.emit({ action: 'RELATED_CLIPS_LOADING', loading: true });
			this.apiClipping
				.getClips(item.groupId, item.clientId || undefined)
				.pipe(take(1))
				.subscribe({
					next: response => {
						if (item.clientId) {
							response.forEach((relatedClip: any) => {
								if (!relatedClip.clientId) relatedClip.clientId = item.clientId;
							});
						}
						if (selectAll) {
							response.forEach((relatedClip: any) => {
								this.selectedItems.push(relatedClip);
								relatedClip.checked = true;
							});
						}
						item.relatedClips = response;

            // item.expanded = Boolean(response.length); // requested to remove auto expand on select all
						item.expanded = response.length ? selectAll !== undefined ? false : !item.expanded: false;
						this.apiLoading = {
							id: '',
							relatedClips: false,
						};
						// Emit loading complete to parent
						this.actionRequested.emit({ action: 'RELATED_CLIPS_LOADING', loading: false });
						this.changeDetection.detectChanges();
					},
					error: () => {
						this.apiLoading = {
							id: '',
							relatedClips: false,
						};
						// Emit loading complete to parent
						this.actionRequested.emit({ action: 'RELATED_CLIPS_LOADING', loading: false });
					},
				});
		} else if (item.relatedClips) {
      // requested to remove auto expand on select all
      // item.expanded = selectAll ? true : !item.expanded;
			// if (!item.expanded) {
			// 	this.selectedItems = [];
			// 	item.checked = false;
			// 	item.relatedClips.forEach((relatedClip: any) => {
			// 		relatedClip.checked = false;
			// 	});
			// 	this.actionRequested.emit({ action: 'UPDATE_SELECTED', selected: this.selectedItems });

			item.expanded = selectAll ? false : !item.expanded;
      if (selectAll !== undefined) {
        item.relatedClips.forEach((relatedClip: any) => {
          relatedClip.checked = selectAll;
        });
        this.actionRequested.emit({ action: 'UPDATE_SELECTED', selected: this.selectedItems });
        return;
      }
		} else {
			item.expanded = false;
		}
	}
}
